
import {
  IEditableObject,
  isText,
  isImage,
  ITextObject,
} from "@/services/Models";
import {
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  reactive,
  ref,
  watch,
} from "vue";
import { ArrowKeySimulate } from "@/lib/arrowKeySimulate";
import { EditStore } from "@/stores";
import { useDraggable } from "@/lib/draggable";
import { useRotate } from "@/lib/rotate";
import { useScale, AnchorPosition } from "@/lib/scale";
import { toResourceUrl, microWait, asyncOnLoad, findChild } from "@/lib/shared";
import sanitizeHTML from "sanitize-html";
import { consts } from "../services/Models";

export default defineComponent({
  name: "EditObject",
  props: {
    object: { type: Object as PropType<IEditableObject> },
    editStore: { type: Object as PropType<EditStore> },
    editorSize: { type: Object as PropType<{ width: number; height: number }> },
    editorScale: { type: Number },
    isSelected: { type: Boolean },
    editorNaturalSize: {
      type: Object as PropType<{ width: number; height: number }>,
    },
  },
  setup(props) {
    const state = reactive({
      tempContent: isText(props.object)
        ? (props.object as ITextObject).content
        : "",
      isEditMode: false,
    });
    const preContent = ref(state.tempContent);

    // ウォーターマーク表示を判定する
    // content.value?.textContentにcomputedが反応しないのでrefでがんばる
    const iswatermark = ref(true);
    const updateWatermark = async () => {
      await microWait();
      let isEmpty = true;
      isEmpty &&= isText(props.object);
      isEmpty &&= !state.isEditMode;
      const textContent = content.value?.textContent ?? "";
      isEmpty &&= !textContent || textContent == consts.hiddenString;
      iswatermark.value = isEmpty;
    };
    watch(() => state.tempContent, updateWatermark);
    watch(() => state.isEditMode, updateWatermark);

    // 位置とサイズは編集エリアに対する相対値にしておかないと環境によってずれる
    // 親から情報をもらってなんとかする
    // そうなるとオブジェクトの位置は中心座標にしとかないとずれる
    const getEditorSizeInfo = () => {
      if (!props.editorSize) return;
      if (!props.editorScale) return;
      return {
        size: props.editorSize,
        scale: props.editorScale,
      };
    };
    // editStoreを経由することで操作を集約してUndoの対処をなるべくしやすくしておく、
    // 移動とかテキスト編集とか連続的な変化は確定するまではこのコンポーネント内の状態でやりくりして確定したら編集オブジェクトを更新する
    // 移動：マウスアップ時
    // テキスト編集はEnterで確定時
    // 移動
    const moveObject = (x: number, y: number) => {
      // 編集領域に対する割合で位置を保存する
      const info = getEditorSizeInfo();
      if (!info) return;
      const px = x / info.size.width;
      const py = y / info.size.height;
      props.editStore?.moveObject(px, py, props.object);
    };
    // テキスト編集
    const setText = (content: string) => {
      props.editStore?.setText(content, props.object);
    };
    // 回転
    const rotateObject = (deg: number) => {
      props.editStore?.rotate(deg, props.object);
    };
    // 拡大
    const scaleObject = (scale: number) => {
      props.editStore?.scale(scale, props.object);
    };
    // 編集開始時
    const startEdit = async () => {
      const editableObject = content.value as HTMLElement;
      const currentText = editableObject.textContent;
      // テキスト未入力なら<p>タグ入れる
      // なぜなら何かしらテキストがないと初期フォーカスが効かないから
      let firstChild: Node;
      let dummyChild: Node;
      if (!currentText) {
        const p = document.createElement("p");
        dummyChild = document.createTextNode("\u{feff}");
        p.appendChild(dummyChild);
        editableObject.appendChild(p);
        firstChild = p;
      } else {
        firstChild = editableObject.firstChild as HTMLElement;
      }
      if (!firstChild) return;
      const firstText =
        findChild(firstChild, (node) => node.nodeType == Node.TEXT_NODE) ??
        firstChild;

      // 先頭にフォーカス移す
      await microWait();
      const sel = window.getSelection();
      if (sel == null) {
        return;
      }
      const range = document.createRange();
      range.setStart(firstText, 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      editableObject.focus();
    };
    const endEdit = () => {
      // 入力内容が空文字だったらクリア
      const editableObject = content.value as HTMLElement;
      const currentText = editableObject.textContent;
      if (!currentText) {
        state.tempContent = "";
        editableObject.innerHTML = "";
        preContent.value = "";
      }
      setText(state.tempContent);
    };
    // ペーストイベントフラグ
    let pasteEvent = false;
    const paste = (e: ClipboardEvent) => {
      pasteEvent = true;
      document.execCommand(
        "insertText",
        false,
        e.clipboardData?.getData("text/plain")
      );
      pasteEvent = false;
      e.stopPropagation();
      e.preventDefault();
    };
    const copy = (e: ClipboardEvent) => {
      const selection = document.getSelection();
      if (selection == null) return;
      // 重複している改行部とBOMを削除しクリップボードに再設定する
      var copyStr = selection.toString();
      copyStr = copyStr.replace(/\ufeff/, "");
      copyStr = copyStr.split("\n\n").join("\n");
      e.clipboardData?.setData("text/plain", copyStr);
      e.preventDefault();
    };
    // モード変化を検知
    watch(
      () => props.isSelected,
      (isSelected: boolean) => {
        // 選択オブジェクトじゃなくなったら編集モードも終了する
        if (!isSelected) {
          state.isEditMode = false;
        }
      }
    );
    watch(
      () => state.isEditMode,
      (isEdit: boolean) => {
        if (!isText(props.object)) return;
        if (isEdit) {
          startEdit();
        } else {
          endEdit();
        }
      }
    );
    // セリフオブジェクトの変更を検知する
    watch(
      () => (props.object as ITextObject)?.content,
      async (v) => {
        const isDiff = state.tempContent != v;
        if (isDiff) {
          state.tempContent = v;
        }
        if (isDiff) {
          preContent.value = "";
          await microWait();
          preContent.value = v;
        }
      }
    );
    //////// windowサイズ変更を検知する
    watch(
      () => props.editorSize,
      () => updatePos()
    );
    watch(
      () => props.editorScale,
      () => updatePos()
    );
    watch(
      () => props.editorNaturalSize,
      () => shrink()
    );

    //オブジェクトの削除を検知する
    watch(
      () => props.object,
      () => shrink()
    );

    //////////////// 自動保存タイマ
    const autoSaveTimer = ref(0);
    const autoSaveInterval = Number.parseInt(
      process.env.VUE_APP_AUTOSAVE_INTERVAL_SECONDS
    );
    const autoSave = () => {
      if (props.isSelected) {
        setText(state.tempContent);
      }
    };
    onMounted(() => {
      autoSaveTimer.value = setInterval(autoSave, autoSaveInterval);
      shrink();
      updateWatermark();
    });
    onUnmounted(() => {
      clearInterval(autoSaveTimer.value);
    });

    //////////
    // キーボードの移動操作
    const moveOn = (e: KeyboardEvent, x: number, y: number) => {
      if (state.isEditMode || !props.isSelected) return;
      tempPos.value = { x: x, y: y };
      moveObject(x, y);
      e.preventDefault();
    };
    const moveUp = (e: KeyboardEvent) => {
      moveOn(e, tempPos.value.x, tempPos.value.y - 1);
    };
    const moveDown = (e: KeyboardEvent) => {
      moveOn(e, tempPos.value.x, tempPos.value.y + 1);
    };
    const moveLeft = (e: KeyboardEvent) => {
      moveOn(e, tempPos.value.x - 1, tempPos.value.y);
    };
    const moveRight = (e: KeyboardEvent) => {
      moveOn(e, tempPos.value.x + 1, tempPos.value.y);
    };

    /////// ドラッグ移動／回転／拡大など特定のモデルに依存したくない操作
    const self = ref<HTMLElement>();
    const content = ref<HTMLElement>();
    const tempPos = ref(consts.initialPos);
    const tempDeg = ref(consts.initialDeg);
    const tempScale = ref(consts.initialScale);

    const updatePos = () => {
      const info = getEditorSizeInfo();
      if (!info) return;
      if (!props.object) return;
      tempPos.value = {
        x: props.object.x * info.size.width,
        y: props.object.y * info.size.height,
      };
    };
    const updateRotate = () => (tempDeg.value = props.object?.rotate ?? 0);
    const updateScale = () => (tempScale.value = props.object?.scale ?? 1);
    watch(() => props.object?.x, updatePos);
    watch(() => props.object?.y, updatePos);
    watch(() => props.object?.rotate, updateRotate);
    watch(() => props.object?.scale, updateScale);
    updatePos();
    updateRotate();
    updateScale();

    const draggable = useDraggable(self, tempPos, moveObject);
    const rotate = useRotate(self, tempDeg, rotateObject);
    const scale = useScale(
      self,
      tempDeg,
      tempPos,
      tempScale,
      () => {
        const imgElem = content.value as HTMLImageElement;
        if (!imgElem) return;
        if (!props.editorScale) return;
        return {
          width: imageLimitWidth.value * props.editorScale,
          height: imageLimitHeight.value * props.editorScale,
        };
      },
      scaleObject,
      moveObject
    );

    const anchors = {
      leftTop: AnchorPosition.LeftTop,
      rightTop: AnchorPosition.RightTop,
      rightBottom: AnchorPosition.RightBottom,
      leftBottom: AnchorPosition.LeftBottom,
    };
    /////// 縦書きモードのカーソルキーを操作を何とかする
    const keySimulator = new ArrowKeySimulate();
    const verticalOn = (f: (_: HTMLElement) => void) => {
      return (e: Event, o: ITextObject) => {
        if (o.isVertical && e.target != null) {
          e.preventDefault();
          f(e.target as HTMLElement);
        }
      };
    };
    const upKey = verticalOn((elm) => keySimulator.Up(elm));
    const rightKey = verticalOn((elm) => keySimulator.Right(elm));
    const downKey = verticalOn((elm) => keySimulator.Down(elm));
    const leftKey = verticalOn((elm) => keySimulator.Left(elm));
    const imageLimitWidth = ref(consts.imageLimitLength);
    const imageLimitHeight = ref(consts.imageLimitLength);

    const shrink = async () => {
      await microWait();
      await microWait();
      if (!isImage(props.object)) return;

      // editorのオリジナルサイズ
      const editorNaturalSize = props.editorNaturalSize;
      if (!editorNaturalSize) return;

      // 画像サイズ
      const image = content.value as HTMLImageElement;
      if (!image) return;

      //画像読み込みを待つ
      await asyncOnLoad(image);

      // 編集領域の80%より大きい画像は縮小する
      const maxScale = consts.objectMaxScale;
      const maxWidth = editorNaturalSize.width * maxScale;
      const maxHeight = editorNaturalSize.height * maxScale;
      if (image.naturalWidth <= maxWidth && image.naturalHeight <= maxHeight) {
        imageLimitWidth.value = image.naturalWidth;
        imageLimitHeight.value = image.naturalHeight;
        return;
      }

      const scaleW = maxWidth / image.naturalWidth;
      const scaleH = maxHeight / image.naturalHeight;
      const scale = Math.min(scaleW, scaleH);

      imageLimitWidth.value = image.naturalWidth * scale;
      imageLimitHeight.value = image.naturalHeight * scale;
    };

    // サニタイズ処理
    const sanitize = (html: string) => {
      return sanitizeHTML(html, {
        allowedTags: ["p", "span", "br"],
        allowedAttributes: {
          p: ["style"],
          span: ["style"],
        },
      });
    };

    // 最大文字数制限
    const limitedMaxLength = (e: KeyboardEvent) => {
      // HACKME もっとありそう
      const allowCodes = ["Delete", "Backspace"];
      if (allowCodes.includes(e.code)) return;
      // 本関数コール時点ではstate.tempContentに入力文字は反映されていないので文字列長超えの比較は「>=」で行う
      if (textLength(state.tempContent) >= consts.maxEditContentLength) {
        // 最大文字数を超えている場合は入力させない
        e.preventDefault();
      }
    };
    // 入力イベント
    const inputText = (element: Element) => {
      // 全角入力はinnerHTMLに入るため、ここでも最大文字数をチェックする
      if (textLength(element.innerHTML) > consts.maxEditContentLength) {
        if (pasteEvent) {
          // ペーストはアラートメッセージ表示
          alert(consts.maxPasteDataSizeOver);
        }
        element.innerHTML = state.tempContent;
      } else {
        state.tempContent = element.innerHTML;
      }
    };
    // 入力文字長取得
    const textLength = (html: string | undefined): number => {
      let text = "";
      if (html !== undefined) {
        // brタグを改行コードに変換し、HTMNLタグとBOM削除
        text = html.replace(/(<br>|<br \/>)/gi, "\n");
        text = text.replace(/<("[^"]*"|'[^']*'|[^'">])*>/g, "");
        text = text.replace(/\ufeff/, "");
      }
      return text.length;
    };
    return {
      self,
      consts,
      content,
      iswatermark,
      draggable,
      rotate,
      scale,
      tempPos,
      tempDeg,
      tempScale,
      anchors,
      state,
      moveUp,
      moveDown,
      moveLeft,
      moveRight,
      setText,
      isText,
      isImage,
      upKey,
      rightKey,
      downKey,
      leftKey,
      toResourceUrl,
      preContent,
      shrink,
      imageLimitWidth,
      imageLimitHeight,
      sanitize,
      limitedMaxLength,
      paste,
      copy,
      inputText,
    };
  },
});
